import React, { useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import ThemeModeToggle from "../components/layout/ThemeModeToggle";
import {
  formatOrgMenuPrimary,
  formatOrgMenuSecondary,
} from "../utils/orgSwitcher";

const TenantSelectionPage: React.FC = () => {
  const { tenants, completeTenantSelection, error } = useAuth();
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSelect = async (tenantId: string) => {
    setLocalError(null);
    setSubmitting(tenantId);
    try {
      await completeTenantSelection(tenantId);
    } catch (err) {
      setLocalError(
        err instanceof Error ? err.message : "Failed to select organization",
      );
    } finally {
      setSubmitting(null);
    }
  };

  const displayError = localError ?? error;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        bgcolor: "background.default",
        py: 4,
        position: "relative",
      }}
    >
      <Box sx={{ position: "absolute", top: 16, right: 16 }}>
        <ThemeModeToggle />
      </Box>
      <Container maxWidth="sm">
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Choose your organization
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select which organization you want to work in for this session.
            </Typography>

            {displayError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {displayError}
              </Alert>
            )}

            <List disablePadding>
              {tenants.map((tenant) => (
                <ListItemButton
                  key={tenant.tenantId}
                  onClick={() => void handleSelect(tenant.tenantId)}
                  disabled={Boolean(submitting)}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <ListItemText
                    primary={formatOrgMenuPrimary(tenant)}
                    secondary={formatOrgMenuSecondary(tenant) ?? undefined}
                  />
                  {submitting === tenant.tenantId && (
                    <CircularProgress size={20} sx={{ ml: 1 }} />
                  )}
                </ListItemButton>
              ))}
            </List>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default TenantSelectionPage;
